import React, { useState } from "react";
import { caseStudies as csData } from "../../data/mock";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

// Localize numeric values for French Canada locale:
//   "99.99%" -> "99,99 %"  (comma decimal + space before %)
//   "$2.1M"  -> "2,1 M$"   (Québec currency notation)
function formatMetric(value, lang) {
  if (lang !== "fr" || typeof value !== "string") return value;
  let out = value.replace(/(\d)\.(\d)/g, "$1,$2");
  out = out.replace(/(\d)%/g, "$1 %");
  out = out.replace(/\$(\d+),(\d+)M/g, "$1,$2 M$$");
  out = out.replace(/\$(\d+)M/g, "$1 M$$");
  return out;
}

export default function CaseStudies() {
  const [active, setActive] = useState(0);
  const { t, lang } = useLanguage();
  const studies = t("caseStudies.studies") || [];
  const current = csData[active];
  const currentText = studies[active] || { category: current.category, title: current.title, challenge: current.challenge, solution: current.solution, metrics: current.metrics.map((m) => m.label) };

  return (
    <section id="case-studies" className="section-bg relative py-24 lg:py-32 overflow-hidden">
      <div className="orb bg-cyan-500/15 w-[500px] h-[500px] top-[10%] right-[-160px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="kicker">{t("caseStudies.kicker")}</span>
          <h2 className="mt-5 font-display text-4xl lg:text-5xl font-bold text-white">
            {t("caseStudies.title1")} <span className="grad-text">{t("caseStudies.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-slate-300/90 text-lg">{t("caseStudies.description")}</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {csData.map((c, i) => (
            <button
              key={c.category}
              onClick={() => setActive(i)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition border ${
                i === active
                  ? "bg-gradient-to-r from-sky-500 to-cyan-400 text-white border-transparent shadow-lg shadow-cyan-500/25"
                  : "border-cyan-400/20 text-slate-300 hover:border-cyan-400/40 hover:text-white bg-white/5"
              }`}
            >
              {(studies[i] && studies[i].category) || c.category}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-stretch">
          <div className="lg:col-span-2 rounded-2xl overflow-hidden glass">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={current.image} alt={currentText.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050818]/90 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 px-3 py-1 text-[11px] tracking-widest uppercase font-semibold rounded-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-100">
                {currentText.category}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 glass rounded-2xl p-8 lg:p-10">
            <h3 className="font-display text-2xl lg:text-3xl font-bold text-white leading-tight">{currentText.title}</h3>

            <div className="mt-6 space-y-5">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-1.5">{t("caseStudies.challenge")}</div>
                <p className="text-slate-300">{currentText.challenge}</p>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-1.5">{t("caseStudies.solution")}</div>
                <p className="text-slate-300">{currentText.solution}</p>
              </div>
            </div>

            <div className="mt-7 grid grid-cols-3 gap-4">
              {current.metrics.map((m, i) => (
                <div key={i} className="rounded-xl border border-cyan-400/15 bg-cyan-500/5 p-4 text-center">
                  <div className="grad-text font-display text-2xl lg:text-3xl font-bold">{formatMetric(m.value, lang)}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-widest text-slate-400">{(currentText.metrics && currentText.metrics[i]) || m.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-7">
              <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">{t("caseStudies.technologies")}</div>
              <div className="flex flex-wrap gap-2">
                {current.tech.map((t2) => (
                  <span key={t2} className="px-3 py-1.5 rounded-lg text-sm bg-white/5 border border-white/10 text-slate-200">{t2}</span>
                ))}
              </div>
            </div>

            <button className="mt-8 inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-semibold">
              {t("caseStudies.viewFull")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
